/*
 * VoxPontProductPicker — presentational list of selectable VoxPont products.
 *
 * Reused by the post-auth onboarding page and the in-app product switcher dialog.
 * Pure UI: it renders the catalog and reports the chosen slug; persistence and
 * navigation are the caller's concern.
 */

import React from "react";
import PropTypes from "prop-types";
import { Stack } from "@mui/material";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { VOXPONT_PRODUCTS } from "features/voxpontProduct/voxpontProducts";

/**
 * @param {Object} props
 * @param {string|null} [props.currentSlug] Slug to render as selected.
 * @param {(slug: string) => void} props.onSelect Called with the chosen product slug.
 * @param {boolean} [props.saving] When true, all cards are disabled.
 * @param {string|null} [props.savingSlug] The slug currently being saved (shows a spinner).
 * @param {boolean} [props.expandable] When true, descriptions are hidden by default and
 *   revealed on hover (tooltip) or via an accordion-style expand arrow.
 */
function VoxPontProductPicker({
  currentSlug = null,
  onSelect,
  saving = false,
  savingSlug = null,
  expandable = false,
}) {
  return (
    <Stack spacing={1.5}>
      {VOXPONT_PRODUCTS.map((product) => {
        const { Icon } = product;
        return (
          <SelectableChoiceCard
            key={product.slug}
            icon={<Icon />}
            title={product.name}
            description={product.description}
            expandable={expandable}
            selected={currentSlug === product.slug}
            loading={saving && savingSlug === product.slug}
            disabled={saving}
            onClick={() => onSelect(product.slug)}
          />
        );
      })}
    </Stack>
  );
}

VoxPontProductPicker.propTypes = {
  currentSlug: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  savingSlug: PropTypes.string,
  expandable: PropTypes.bool,
};

export default VoxPontProductPicker;
